/**
 * The viewer's single WebSocket. Reconnects automatically and re-sends
 * subscriptions. Meter frames and goniometer pairs are dispatched to listeners
 * without going through React state.
 */
import { BINARY_KIND_PAIRS, PAIRS_HEADER_BYTES, type ClientMessage, type PairRef, type ServerMessage } from '@shared/protocol';

export interface PairsFrame {
  streamId: number;
  track: number;
  a: number;
  b: number;
  rate: number;
  /** Interleaved A,B samples, full scale = 1.0. */
  samples: Float32Array;
}

type MessageListener = (msg: ServerMessage) => void;
type PairsListener = (frame: PairsFrame) => void;
type ConnectionListener = (connected: boolean) => void;

export function decodePairs(buf: ArrayBuffer): PairsFrame | null {
  if (buf.byteLength < PAIRS_HEADER_BYTES) return null;
  const view = new DataView(buf);
  if (view.getUint8(0) !== BINARY_KIND_PAIRS) return null;
  const n = (buf.byteLength - PAIRS_HEADER_BYTES) / 2;
  const ints = new Int16Array(buf, PAIRS_HEADER_BYTES, n);
  const samples = new Float32Array(n);
  for (let i = 0; i < n; i++) samples[i] = ints[i] / 32767;
  return {
    a: view.getUint8(1),
    b: view.getUint8(2),
    track: view.getUint8(3),
    streamId: view.getUint32(4, true),
    rate: view.getUint32(8, true),
    samples,
  };
}

class MonitorSocket {
  private ws: WebSocket | null = null;
  private readonly messageListeners = new Set<MessageListener>();
  private readonly pairsListeners = new Set<PairsListener>();
  private readonly connectionListeners = new Set<ConnectionListener>();
  private readonly subscriptions = new Map<number, { pair: PairRef; refs: number }>();
  private watchingStatus = 0;
  private retry = 0;
  private timer: number | null = null;
  private wanted = false;
  connected = false;

  start(): void {
    this.wanted = true;
    if (!this.ws) this.connect();
  }

  stop(): void {
    this.wanted = false;
    if (this.timer) window.clearTimeout(this.timer);
    this.ws?.close();
    this.ws = null;
  }

  private connect(): void {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      this.setConnected(true);
      if (this.watchingStatus > 0) this.send({ type: 'watchStatus' });
      for (const [streamId, s] of this.subscriptions) this.send({ type: 'subscribe', streamId, pair: s.pair });
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        const msg = JSON.parse(ev.data) as ServerMessage;
        for (const l of this.messageListeners) l(msg);
      } else {
        const frame = decodePairs(ev.data as ArrayBuffer);
        if (frame) for (const l of this.pairsListeners) l(frame);
      }
    };
    ws.onclose = () => {
      this.ws = null;
      this.setConnected(false);
      if (!this.wanted) return;
      const delay = Math.min(15_000, 500 * 2 ** this.retry++);
      this.timer = window.setTimeout(() => this.connect(), delay);
    };
  }

  private setConnected(v: boolean): void {
    this.connected = v;
    for (const l of this.connectionListeners) l(v);
  }

  private send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  subscribe(streamId: number, pair: PairRef = { track: 0, a: 0, b: 1 }): () => void {
    const cur = this.subscriptions.get(streamId);
    if (cur && cur.pair.track === pair.track && cur.pair.a === pair.a && cur.pair.b === pair.b) {
      cur.refs++;
    } else {
      this.subscriptions.set(streamId, { pair, refs: (cur?.refs ?? 0) + 1 });
      this.send({ type: 'subscribe', streamId, pair });
    }
    return () => {
      const s = this.subscriptions.get(streamId);
      if (!s) return;
      if (--s.refs <= 0) {
        this.subscriptions.delete(streamId);
        this.send({ type: 'unsubscribe', streamId });
      }
    };
  }

  watchStatus(): () => void {
    if (this.watchingStatus++ === 0) this.send({ type: 'watchStatus' });
    return () => {
      this.watchingStatus = Math.max(0, this.watchingStatus - 1);
    };
  }

  onMessage(l: MessageListener): () => void {
    this.messageListeners.add(l);
    return () => this.messageListeners.delete(l);
  }

  onPairs(l: PairsListener): () => void {
    this.pairsListeners.add(l);
    return () => this.pairsListeners.delete(l);
  }

  onConnection(l: ConnectionListener): () => void {
    this.connectionListeners.add(l);
    return () => this.connectionListeners.delete(l);
  }
}

export const socket = new MonitorSocket();
